import React, {useEffect, useState, useMemo} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import Util from '../../../forecast-app/shared/util/util';
import InlineLoader from '../../../forecast-app/shared/components/inline-loader/inline_loader';
import ImportSageIntacctCustomersMutation from '../../../mutations/ImportSageIntacctCustomersMutation';
import {hideLoader, showLoader} from '../../global_loader';
import DirectApi from '../../../directApi';
import {
	Checkbox,
	DeprecatedCreativeModal as CreativeModal,
	DeprecatedScrollbar as Scrollbar,
	FlexColumn,
	FlexRow,
	SubHeading,
	Text,
} from '@forecast-it/design-system';
import styled from 'styled-components/macro';
import {CheckedState} from '@radix-ui/react-checkbox';
import {createToast} from '../../../forecast-app/shared/components/toasts/another-toast/toaster';
import {HorizontalBar, ListWrapper, ModalContentWrapper} from './SageIntacctModal.styled';
import {calculateScrollMaxHeight} from './SageIntacctImportTeamMembersModal';

type ListItemType = {
	hasBorderBottom?: boolean;
};

const ListItem = styled.div<ListItemType>`
	width: 550px;
	padding: 18px 8px 18px 8px;
	border-bottom: ${({hasBorderBottom}) => (hasBorderBottom ? '1px solid #ccc' : 'none')};
`;

type SageIntacctImportCustomersModalProps = {
	closeModal: () => void;
	companyId: string;
};

type SageIntacctCustomer = {
	CUSTOMERID: string;
	NAME: string;
};

const SageIntacctImportCustomersModal = ({closeModal, companyId}: SageIntacctImportCustomersModalProps): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const [sageIntacctCustomers, setSageIntacctCustomers] = useState<SageIntacctCustomer[]>([]);
	const [fetchingCustomers, setFetchingCustomers] = useState(true);
	const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
	const [selectAll, setSelectAll] = useState<CheckedState>(false);
	const [importing, setImporting] = useState(false);
	const [failed, setFailed] = useState(false);
	const [limitReached, setLimitReached] = useState(false);

	const customerSelectLimit = 100;

	const scrollMaxHeight = useMemo(() => calculateScrollMaxHeight(400), []);

	const disableSyncButton = importing || selectedCustomers.length === 0 || limitReached;

	useEffect(() => {
		DirectApi.Fetch('sage_intacct/unimported_customers').then(res => {
			if (res?.customers) {
				setSageIntacctCustomers(
					res.customers.sort((a, b) => (a.NAME.toUpperCase().trim() > b.NAME.toUpperCase().trim() ? 1 : -1))
				);
				setFetchingCustomers(false);
			} else if (res?.error) {
				setFailed(true);
				setFetchingCustomers(false);
			} else {
				setSageIntacctCustomers([]);
				setFetchingCustomers(false);
			}
		});
	}, []);

	const toggleSelectAll = () => {
		if (selectAll) {
			setSelectedCustomers([]);
			setSelectAll(false);
			setLimitReached(false);
		} else {
			setSelectedCustomers(sageIntacctCustomers.slice(0, customerSelectLimit).map(customer => customer.CUSTOMERID));
			setSelectAll(true);
		}
	};

	const toggleCustomer = (sageIntacctCustomerId: string) => {
		setSelectAll('indeterminate');

		let newSelectedCustomers;
		if (selectedCustomers.includes(sageIntacctCustomerId)) {
			newSelectedCustomers = selectedCustomers.filter(id => id !== sageIntacctCustomerId);
			setSelectedCustomers(newSelectedCustomers);
			if (newSelectedCustomers.length === 0) {
				setSelectAll(false);
			}
		} else {
			newSelectedCustomers = [...selectedCustomers, sageIntacctCustomerId];
			if (newSelectedCustomers.length === sageIntacctCustomers.length) {
				setSelectAll(true);
			}
			setSelectedCustomers(newSelectedCustomers);
		}

		if (newSelectedCustomers.length > customerSelectLimit) {
			setLimitReached(true);
		} else {
			setLimitReached(false);
		}
	};

	const syncContacts = () => {
		setImporting(true);
		showLoader();
		Util.CommitMutation(
			ImportSageIntacctCustomersMutation,
			{
				sageIntacctCustomerIds: selectedCustomers,
				companyId: companyId,
			},
			() => {
				setImporting(false);
				closeModal();
				hideLoader();
				createToast({
					duration: 5000,
					message: `${selectedCustomers.length} Clients imported from Sage Intacct`,
				});
			}
		);
	};

	return (
		<CreativeModal
			title={<FormattedMessage id="integrations.sage_intacct.sync_clients" />}
			size={'l'}
			closeModal={closeModal}
			creativeButtonText={`Import Clients (${selectedCustomers.length})`}
			cancelButtonText={formatMessage({id: 'common.cancel'})}
			onCreateHandler={syncContacts}
			onCancelHandler={closeModal}
			disabled={disableSyncButton}
			replicateDesignSystem
		>
			<ModalContentWrapper>
				<FlexColumn gap={'xs'}>
					<Text color={Text.color.medium}>
						{formatMessage({id: 'integrations.sage_intacct.sync_clients.description'})}
					</Text>
				</FlexColumn>
				{failed && <Text color={'error'}>{formatMessage({id: 'integrations.sage_intacct.sync_error_message'})}</Text>}
				{limitReached && (
					<Text color={'error'}>
						{formatMessage(
							{id: 'integrations.sage_intacct.customer_selection_limit_message'},
							{limit: customerSelectLimit}
						)}
					</Text>
				)}
				<ListItem>
					<FlexRow gap={'xl'}>
						<Checkbox onChange={toggleSelectAll} checked={selectAll} />
						<SubHeading size={'2'}>
							{sageIntacctCustomers.length > customerSelectLimit
								? formatMessage(
										{id: 'integrations.sage_intacct.select_all_button_text'},
										{limit: customerSelectLimit}
								  )
								: formatMessage({id: 'common.select_all'})}
						</SubHeading>
					</FlexRow>
				</ListItem>
				<ListWrapper maxHeight={scrollMaxHeight}>
					{fetchingCustomers ? (
						<InlineLoader />
					) : (
						<Scrollbar maxHeight={`${scrollMaxHeight}px`}>
							<FlexColumn alignItems="flex-start" justifyContent="flex-start">
								{sageIntacctCustomers &&
									sageIntacctCustomers.map((cust: SageIntacctCustomer) => {
										return (
											<ListItem key={'department-' + cust.CUSTOMERID} hasBorderBottom>
												<FlexRow gap={'xl'}>
													<Checkbox
														onChange={() => toggleCustomer(cust.CUSTOMERID)}
														checked={selectedCustomers.includes(cust.CUSTOMERID)}
													/>
													<Text size={'2'}>{cust.NAME}</Text>
												</FlexRow>
											</ListItem>
										);
									})}
							</FlexColumn>
						</Scrollbar>
					)}
				</ListWrapper>
			</ModalContentWrapper>
			<HorizontalBar />
		</CreativeModal>
	);
};

export default SageIntacctImportCustomersModal;
